import { ISO_DAY } from '../../lib/dateInput';
import { parseCircuitKind, requiresParapheBoxes, type CircuitKind } from '../../lib/signature/circuitKind';

export const AWAITING_REVISION = 'awaiting_revision' as const;
export const CIRCUIT_DRAFT = 'draft' as const;

export function statusAfterRefuse(): typeof AWAITING_REVISION {
  return AWAITING_REVISION;
}

export function nextDocumentVersion(current: number | null | undefined): number {
  const n = typeof current === 'number' && Number.isFinite(current) && current >= 1 ? current : 1;
  return n + 1;
}

export interface StepRevisionState {
  status: string;
  comment: string | null;
  paraphed_zone_ids: string[];
}

export function resetStepsForNewVersion(steps: StepRevisionState[]): StepRevisionState[] {
  return steps.map(() => ({
    status: 'pending',
    comment: null,
    paraphed_zone_ids: [],
  }));
}

export function canResubmitRevision(args: {
  status: string;
  isInitiator: boolean;
  isAdmin: boolean;
}): boolean {
  return args.status === AWAITING_REVISION && (args.isInitiator || args.isAdmin);
}

export function documentChanged(
  baselineStoragePath: string | null | undefined,
  currentStoragePath: string | null | undefined,
): boolean {
  if (!baselineStoragePath || !currentStoragePath) return false;
  return baselineStoragePath !== currentStoragePath;
}

export function canResendCorrected(args: {
  status: string;
  isInitiator: boolean;
  isAdmin: boolean;
  baselineStoragePath: string | null | undefined;
  currentStoragePath: string | null | undefined;
  boxCount: number;
  kind?: CircuitKind | string | null;
}): boolean {
  const kind = parseCircuitKind(args.kind);
  const boxesOk = requiresParapheBoxes(kind) ? args.boxCount >= 1 : true;
  return canResubmitRevision(args)
    && documentChanged(args.baselineStoragePath, args.currentStoragePath)
    && boxesOk;
}

export type VisaFileAction = 'demander' | 'demander_validation' | 'ouvrir' | 'corriger';

export const VISA_FILE_ACTION_LABEL: Record<VisaFileAction, string> = {
  demander: 'Demander un visa',
  demander_validation: 'Demander une validation',
  ouvrir: 'Ouvrir',
  corriger: 'Corriger',
};

export function visaFileMenu(args: {
  linkedStatus: string | null | undefined;
  isInitiator: boolean;
  isAdmin: boolean;
}): VisaFileAction[] {
  const status = args.linkedStatus ?? null;
  const startActions: VisaFileAction[] = ['demander', 'demander_validation'];
  if (!status || status === 'cancelled' || status === 'rejected') {
    return startActions;
  }
  if (status === 'in_progress' || status === CIRCUIT_DRAFT) return ['ouvrir'];
  if (status === AWAITING_REVISION) {
    return args.isInitiator || args.isAdmin ? ['corriger'] : ['ouvrir'];
  }
  if (status === 'completed') return ['ouvrir', ...startActions];
  return startActions;
}

export function canStartNewCircuitOnFile(existingStatus: string | null | undefined): boolean {
  return visaFileMenu({
    linkedStatus: existingStatus,
    isInitiator: true,
    isAdmin: false,
  }).includes('demander');
}

export function canWithdrawVisaRequest(args: {
  status: string;
  isInitiator: boolean;
  isAdmin: boolean;
}): boolean {
  return (args.isInitiator || args.isAdmin)
    && (args.status === CIRCUIT_DRAFT || args.status === 'in_progress' || args.status === AWAITING_REVISION);
}

/** Modal Demander un visa : titre, ≥1 signataire, échéance obligatoire sur chaque ligne. */
export function canSubmitVisaRequest(args: {
  title: string;
  signers: Array<{ collaborator_id: string; deadline_at: string }>;
}): boolean {
  if (!args.title.trim()) return false;
  if (args.signers.length === 0) return false;
  return args.signers.every((s) => s.collaborator_id.length > 0 && ISO_DAY.test(s.deadline_at));
}

/** Envoyer aux signataires : draft + ≥1 case, initiateur / admin. */
export function canDispatchVisaRequest(args: {
  status: string;
  isInitiator: boolean;
  isAdmin: boolean;
  boxCount: number;
}): boolean {
  return args.status === CIRCUIT_DRAFT
    && (args.isInitiator || args.isAdmin)
    && args.boxCount >= 1;
}

export function parseDraftIdFromSourceRef(sourceRef: string | null | undefined): string | null {
  if (!sourceRef || !sourceRef.startsWith('draft::')) return null;
  const id = sourceRef.slice('draft::'.length).trim();
  return id.length > 0 ? id : null;
}

const TO_SIGN_CIRCUIT_STATUSES = new Set(['in_progress']);
const MES_ENVOIS_STATUSES = new Set(['draft', 'in_progress', 'awaiting_revision', 'completed']);

/** À signer : mes étapes pending sur une demande encore en cours. */
export function toSignInbox<T extends { circuit: { status: string } | null }>(steps: T[]): T[] {
  return steps.filter((s) => s.circuit != null && TO_SIGN_CIRCUIT_STATUSES.has(s.circuit.status));
}

/** Mes envois : demandes que j’ai lancées, hors retiré / annulé / rejeté mort. */
export function mesEnvoisInbox<T extends { status: string }>(initiated: T[]): T[] {
  return initiated.filter((c) => MES_ENVOIS_STATUSES.has(c.status));
}
